import jsPDF from 'jspdf';
import apiDocumentos from '../api/Documentos/api-documentos';
import { PDF_PW, PDF_PH, PDF_MG, PDF_CW } from './pdfPage';
import { esDeclaracionUnificada } from './documentosGrupoConformado';

/**
 * Documentos administrativos de una postulación en PDF: declaración juramentada de residencia,
 * relación de participantes, carta de conformación de grupo y declaración para grupos conformados.
 *
 * Vive aquí porque los generan por igual la vista del participante (Mis postulaciones) y la del
 * administrador (Gestionar postulaciones); antes el mismo código estaba copiado en ambas.
 */

/** Datos de la postulación que aparecen en los documentos, sin importar quién los descargue. */
export type DatosPostulacionPDF = {
  lineaConvocatoria: any;
  numeroDocumento: any;
};

/**
 * Quién firma la declaración de residencia. Cambia según la vista: el participante toma su propio
 * nombre y el tipo de identificación de su sesión; el administrador, el del representante y el
 * tipo guardado en el documento.
 */
export type DeclaranteResidencia = {
  nombre: any;
  tipoIdentificacion: any;
};

const _PW = PDF_PW, _PH = PDF_PH, _MG = PDF_MG, _CW = PDF_CW;

/**
 * Los parrafos del cuerpo van justificados, como el formato impreso.
 *
 * jsPDF no estira la ultima linea de cada llamada, y aqui se pinta un
 * parrafo por llamada, asi que cada uno cierra a bandera como debe ser.
 */
const PARRAFO = { maxWidth: _CW, align: 'justify' as const };
const _str = (v: any) => (v == null ? '' : String(v));

export const obtenerFirmaB64 = async (firma: string): Promise<string> => {
  if (!firma) return '';
  try {
    const data = await apiDocumentos.consultarArchivo(firma);
    if (data?.data) {
      return new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(data.data);
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => resolve('');
      });
    }
  } catch { /* */ }
  return '';
};

const renderParticipante = (pdf: jsPDF, participante: any, firmaB64: string | undefined, y: number): number => {
  const LH = 4; const halfW = _CW / 2;
  pdf.setFontSize(8.5);
  const cols: [string, string][] = [
    ['Nombres:', _str(participante.nombresApellidos)], ['Rol:', _str(participante.rol)],
    ['Tipo Doc:', _str(participante.tipoDocumento)], ['N° Doc:', _str(participante.numeroDocumento)],
    ['Fecha Nac:', _str(participante.fechaNacimiento)], ['Edad:', _str(participante.edadCumplida)],
    ['Dirección:', _str(participante.direccionResidencia)], ['Ciudad:', _str(participante.ciudad)],
    ['Tel. Fijo:', _str(participante.telefonoFijo)], ['Tel. Cel:', _str(participante.telefonoCelular)],
    ['Residencia:', _str(participante.tiempoResidencia) + ' años'], ['Correo:', _str(participante.correoElectronico)],
  ];
  const rows = Math.ceil(cols.length / 2);
  const firmH = firmaB64 ? 16 : 0;
  const cardH = 4 + rows * LH + firmH + 4;
  if (y + cardH > _PH - _MG) { pdf.addPage(); y = _MG; }
  pdf.setDrawColor(200, 200, 200); pdf.setFillColor(250, 250, 250);
  pdf.rect(_MG, y, _CW, cardH, 'FD');
  let ty = y + 4;
  for (let i = 0; i < cols.length; i += 2) {
    const [lL, vL] = cols[i]; const pair = cols[i + 1];
    pdf.setFont('helvetica', 'bold'); pdf.text(lL, _MG + 2, ty);
    pdf.setFont('helvetica', 'normal');
    const vLt = (pdf.splitTextToSize(vL, halfW - pdf.getTextWidth(lL + ' ') - 2)[0]) || '';
    pdf.text(vLt, _MG + 2 + pdf.getTextWidth(lL + ' '), ty);
    if (pair) {
      const [lR, vR] = pair;
      pdf.setFont('helvetica', 'bold'); pdf.text(lR, _MG + halfW + 2, ty);
      pdf.setFont('helvetica', 'normal');
      const vRt = (pdf.splitTextToSize(vR, halfW - pdf.getTextWidth(lR + ' ') - 2)[0]) || '';
      pdf.text(vRt, _MG + halfW + 2 + pdf.getTextWidth(lR + ' '), ty);
    }
    ty += LH;
  }
  if (firmaB64) {
    try { pdf.addImage(firmaB64, firmaB64.startsWith('data:image/jpe') ? 'JPEG' : 'PNG', _MG + halfW - 12, ty + 1, 20, 10); } catch { /* */ }
  }
  return y + cardH + 3;
};

export const construirDeclaracionResidencia = (dd: any, firmaB64: string, datos: DatosPostulacionPDF, declarante: DeclaranteResidencia): Blob => {
  const pdf = new jsPDF('p', 'mm', 'a4'); const LH = 5;
  const chk = (yy: number, n: number) => { if (yy + n > _PH - _MG) { pdf.addPage(); return _MG; } return yy; };
  let y = _MG + 5;
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(13); pdf.setTextColor(0, 0, 0);
  pdf.text('DECLARACIÓN JURAMENTADA DE RESIDENCIA', _PW / 2, y, { align: 'center' }); y += 10;
  pdf.setFontSize(10); pdf.setFont('helvetica', 'normal');
  const isSecMujeres = _str(datos.lineaConvocatoria).includes('SM -');
  const p1 = isSecMujeres
    ? ['Señores,', 'Convocatoria Estímulos a los Talentos y Liderazgos de las Mujeres', 'Secretaría de las Mujeres', 'Alcaldía de Medellín', 'Distrito de Ciencia, Tecnología e Innovación']
    : ['Señores,', 'Convocatoria de Fomento y Estímulos para el Arte y la Cultura', 'Secretaría de Cultura Ciudadana', 'Alcaldía de Medellín', 'Distrito de Ciencia, Tecnología e Innovación'];
  y = chk(y, p1.length * LH + 5); p1.forEach(l => { pdf.text(l, _MG, y); y += LH; }); y += 5;
    const p2 = `Yo, ${_str(declarante.nombre)}, identificado(a) con ${_str(declarante.tipoIdentificacion)} número ${_str(datos.numeroDocumento)}, manifiesto bajo gravedad de juramento que mi lugar de residencia es ${_str(dd?.ciudad)} y lo ha sido durante los últimos ${_str(dd?.anos_residencia)} años.`;
  const p2L = pdf.splitTextToSize(p2, _CW); y = chk(y, p2L.length * LH + 5); pdf.text(p2L, _MG, y); y += p2L.length * LH + 5;
  const campos: [string, string][] = [['Dirección:', _str(dd?.direccion)], ['Ciudad:', _str(dd?.ciudad)]];
  if (_str(dd?.ciudad).toUpperCase() === 'MEDELLÍN') campos.push(['Comuna:', _str(dd?.comuna)]);
  campos.push(['Correo electrónico:', _str(dd?.correo)], ['Teléfono:', _str(dd?.telefono)]);
  for (const [lbl, val] of campos) { y = chk(y, LH + 2); pdf.setFont('helvetica', 'bold'); pdf.text(lbl, _MG, y); pdf.setFont('helvetica', 'normal'); pdf.text(val, _MG + pdf.getTextWidth(lbl + ' '), y); y += LH + 1; }
  y += 5;
  const ftL = pdf.splitTextToSize(`En constancia, se firma a los ${_str(dd?.fecha_firma)}`, _CW);
  y = chk(y, ftL.length * LH + 25); pdf.text(ftL, _MG, y); y += ftL.length * LH + 3;
  if (firmaB64) { try { pdf.addImage(firmaB64, firmaB64.startsWith('data:image/jpe') ? 'JPEG' : 'PNG', _MG, y, 50, 20); } catch { /* */ } }
  return pdf.output('blob');
};

export const construirRelacion = (dd: any, firmasMap: {[k: number]: string}, firmaRepB64: string, datos: DatosPostulacionPDF): Blob => {
  const pdf = new jsPDF('p', 'mm', 'a4'); const LH = 5;
  const chk = (yy: number, n: number) => { if (yy + n > _PH - _MG) { pdf.addPage(); return _MG; } return yy; };
  let y = _MG + 5;
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(13); pdf.setTextColor(0, 0, 0);
  pdf.text('RELACIÓN DE PARTICIPANTES DE LA PROPUESTA', _PW / 2, y, { align: 'center' }); y += 10;
  pdf.setFontSize(10); pdf.setFont('helvetica', 'normal');
  ['Señores,', 'Convocatoria de Fomento y Estímulos para el Arte y la Cultura', 'Secretaría de Cultura Ciudadana', 'Alcaldía de Medellín', 'Distrito de Ciencia, Tecnología e Innovación']
    .forEach(l => { pdf.text(l, _MG, y); y += LH; }); y += 3;
  pdf.setFont('helvetica', 'bold'); pdf.text('Asunto: Relación de participantes para propuestas de personas jurídicas', _MG, y); y += LH + 3;
  pdf.setFont('helvetica', 'normal');
  const pL = pdf.splitTextToSize(`A continuación, relacionamos las personas que desarrollarán la propuesta ${_str(dd?.datos_empresa?.razon_social)} presentada en la ${_str(datos.lineaConvocatoria)}.`, _CW);
  pdf.text(pL, _MG, y); y += pL.length * LH + 5;
  if (dd?.participantes?.length > 0) { for (let i = 0; i < dd.participantes.length; i++) { y = renderParticipante(pdf, dd.participantes[i], firmasMap[i], y); } }
  y += 3;
  const ftL = pdf.splitTextToSize(`En constancia, se firma en Medellín a los ${_str(dd?.fecha_firma)}.`, _CW);
  y = chk(y, ftL.length * LH + 30); pdf.text(ftL, _MG, y); y += ftL.length * LH + 5;
  if (firmaRepB64) { try { pdf.addImage(firmaRepB64, firmaRepB64.startsWith('data:image/jpe') ? 'JPEG' : 'PNG', _MG, y, 50, 20); } catch { /* */ } y += 22; }
  pdf.setFont('helvetica', 'bold'); pdf.text(_str(dd?.participantes?.[0]?.nombresApellidos), _MG, y); y += LH;
  pdf.setFont('helvetica', 'normal'); pdf.text('Representante legal', _MG, y);
  return pdf.output('blob');
};

export const construirCarta = (dd: any, firmasMap: {[k: number]: string}, firmaRepB64: string, datos: DatosPostulacionPDF): Blob => {
  const pdf = new jsPDF('p', 'mm', 'a4'); const LH = 5;
  const chk = (yy: number, n: number) => { if (yy + n > _PH - _MG) { pdf.addPage(); return _MG; } return yy; };
  let y = _MG + 5;
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(13); pdf.setTextColor(0, 0, 0);
  pdf.text('CARTA DE CONFORMACIÓN DE GRUPO', _PW / 2, y, { align: 'center' }); y += 10;
  pdf.setFontSize(10); pdf.setFont('helvetica', 'normal');
  ['Señores,', 'Convocatoria de Fomento y Estímulos para el Arte y la Cultura', 'Secretaría de Cultura Ciudadana', 'Alcaldía de Medellín', 'Distrito de Ciencia, Tecnología e Innovación']
    .forEach(l => { pdf.text(l, _MG, y); y += LH; }); y += 3;
  const rep = dd?.participantes?.[0];
  for (const p of [
    `Los suscritos abajo firmantes, manifestamos por medio de este documento, que conformamos el grupo ${_str(rep?.nombreGrupo)} y que hemos decidido participar en la línea ${_str(datos.lineaConvocatoria)} para lo cual afirmamos conocer las condiciones generales y específicas de participación.`,
    `Para efectos del proceso de la convocatoria, designamos como representante del grupo a ${_str(rep?.nombresApellidos)} identificado(a) con ${_str(rep?.tipoDocumento)} número ${_str(rep?.numeroDocumento)}, quien está expresamente facultado(a) para firmar, presentar la propuesta, y en caso de resultar beneficiarios de un estímulo, firmar los documentos necesarios y tomar todas las decisiones que fueren necesarias.`,
    `Además, manifestamos bajo gravedad de juramento que ninguno de los integrantes del grupo relacionados a continuación, incluido el representante, está incurso en las limitantes establecidas en la convocatoria.`,
  ]) { const ls = pdf.splitTextToSize(p, _CW); y = chk(y, ls.length * LH + 3); pdf.text(ls, _MG, y, PARRAFO); y += ls.length * LH + 4; }
  y += 3;
  if (dd?.participantes?.length > 0) { for (let i = 0; i < dd.participantes.length; i++) { y = renderParticipante(pdf, dd.participantes[i], firmasMap[i], y); } }
  y += 3;
  const ftL = pdf.splitTextToSize(`En constancia, se firma en Medellín a los ${_str(dd?.fecha_firma)}.`, _CW);
  y = chk(y, ftL.length * LH + 30); pdf.text(ftL, _MG, y); y += ftL.length * LH + 5;
  if (firmaRepB64) { try { pdf.addImage(firmaRepB64, firmaRepB64.startsWith('data:image/jpe') ? 'JPEG' : 'PNG', _MG, y, 50, 20); } catch { /* */ } y += 22; }
  pdf.setFont('helvetica', 'bold'); pdf.text(_str(rep?.nombresApellidos), _MG, y); y += LH;
  pdf.setFont('helvetica', 'normal'); pdf.text('Representante del grupo', _MG, y);
  return pdf.output('blob');
};

/** El titulo de los dos formatos aparte, el que ya se firmo asi. */
export const TITULO_DECLARACION_RESIDENCIA_GRUPO =
  'DECLARACIÓN JURAMENTADA DE RESIDENCIA PARA GRUPOS CONFORMADOS';

/** El del unificado, que nombra tambien la conformacion del grupo. */
export const TITULO_DECLARACION_UNIFICADA =
  'DECLARACIÓN DE RESIDENCIA Y CONFORMACIÓN DEL GRUPO';

/** La llamada que acompaña a "gravedad de juramento" en el unificado. */
export const LLAMADA_JURAMENTO = '¹';

/** Lo que advierte esa llamada, al pie de la pagina donde aparece. */
export const NOTA_FALSO_TESTIMONIO =
  '¹ Artículo. 442 del código penal. FALSO TESTIMONIO. El que, en actuación judicial o administrativa, bajo la gravedad del juramento ante autoridad competente, falte a la verdad o la calle total o parcialmente, incurrirá en prisión de seis (6) a doce (12) años.';

/** Las notas de validez, despues de la firma del representante. */
export const NOTAS_DEL_FORMATO = [
  'El presente documento sólo es válido con la totalidad de las firmas y completamente diligenciado.',
  'Los nombres relacionados en el presente documento deberán coincidir con los inscritos en el formulario y los documentos de identidad aportados.',
];

/**
 * La nota al pie, en la pagina donde quedo la llamada.
 *
 * Se dibuja al final, cuando ya se sabe cuantas paginas hay, y se vuelve a
 * esa pagina para ponerla abajo, separada por una raya como en el formato
 * impreso.
 */
const pintarNotaAlPie = (pdf: jsPDF, pagina: number) => {
  pdf.setPage(pagina);
  pdf.setFont('helvetica', 'italic'); pdf.setFontSize(7.5); pdf.setTextColor(0, 0, 0);
  const lineas = pdf.splitTextToSize(NOTA_FALSO_TESTIMONIO, _CW);
  const yTexto = _PH - _MG - lineas.length * 3.2;
  pdf.setLineWidth(0.3);
  pdf.line(_MG, yTexto - 3, _MG + 55, yTexto - 3);
  pdf.text(lineas, _MG, yTexto);
};

export const construirDeclaracionGC = (dd: any, firmasMap: {[k: number]: string}, firmaRepB64: string, datos: DatosPostulacionPDF): Blob => {
  const pdf = new jsPDF('p', 'mm', 'a4'); const LH = 5;
  const chk = (yy: number, n: number) => { if (yy + n > _PH - _MG) { pdf.addPage(); return _MG; } return yy; };
  let y = _MG + 5;
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11); pdf.setTextColor(0, 0, 0);
  /*
    El formato unificado lleva la conformacion en el titulo; lo que se
    diligencio con los dos formatos aparte conserva el suyo, para que
    consultarlo siga dando el documento que se firmo.
  */
  const unificado = esDeclaracionUnificada(dd);
  const titulo = unificado
    ? TITULO_DECLARACION_UNIFICADA
    : TITULO_DECLARACION_RESIDENCIA_GRUPO;
  const titleLines = pdf.splitTextToSize(titulo, _CW);
  pdf.text(titleLines, _PW / 2, y, { align: 'center' }); y += titleLines.length * 6 + 4;
  pdf.setFontSize(10); pdf.setFont('helvetica', 'normal');
  ['Señores,', 'Convocatoria de Fomento y Estímulos para el Arte y la Cultura', 'Secretaría de Cultura Ciudadana', 'Alcaldía de Medellín', 'Distrito de Ciencia, Tecnología e Innovación']
    .forEach(l => { pdf.text(l, _MG, y); y += LH; }); y += 3;
  const rep = dd?.participantes?.[0];
  for (const p of [
    `Los suscritos abajo firmantes, manifestamos por medio de este documento, que conformamos el grupo ${_str(rep?.nombreGrupo)} y que hemos decidido participar en la línea ${_str(datos.lineaConvocatoria)} para lo cual afirmamos conocer las condiciones generales y específicas de participación.`,
    `Para efectos del proceso de la convocatoria, designamos como representante del grupo a ${_str(rep?.nombresApellidos)} identificado(a) con ${_str(rep?.tipoDocumento)} número ${_str(rep?.numeroDocumento)}, quien está expresamente facultado(a) para firmar, presentar la propuesta, y en caso de resultar beneficiarios de un estímulo, firmar los documentos necesarios y tomar todas las decisiones que fueren necesarias.`,
    'También, manifestamos que ninguno de los integrantes del grupo está incurso en las limitantes establecidas en la convocatoria.',
    `Además, los abajo firmantes, manifestamos la bajo gravedad de juramento${unificado ? LLAMADA_JURAMENTO : ''}, que nuestros lugares de residencia son los que se relacionan a continuación:`,
  ]) { const ls = pdf.splitTextToSize(p, _CW); y = chk(y, ls.length * LH + 3); pdf.text(ls, _MG, y, PARRAFO); y += ls.length * LH + 4; }
  // La llamada queda en esta pagina; la nota al pie va aqui abajo.
  const paginaDeLaLlamada = pdf.getNumberOfPages();
  y += 3;
  if (dd?.participantes?.length > 0) { for (let i = 0; i < dd.participantes.length; i++) { y = renderParticipante(pdf, dd.participantes[i], firmasMap[i], y); } }
  y += 3;
  const ftL = pdf.splitTextToSize(`En constancia, se firma en Medellín a los ${_str(dd?.fecha_firma)}.`, _CW);
  y = chk(y, ftL.length * LH + 30); pdf.text(ftL, _MG, y); y += ftL.length * LH + 5;
  if (firmaRepB64) { try { pdf.addImage(firmaRepB64, firmaRepB64.startsWith('data:image/jpe') ? 'JPEG' : 'PNG', _MG, y, 50, 20); } catch { /* */ } y += 22; }
  pdf.setFont('helvetica', 'bold'); pdf.text(_str(rep?.nombresApellidos), _MG, y); y += LH;
  pdf.setFont('helvetica', 'normal'); pdf.text('Representante del grupo', _MG, y); y += LH;

  if (unificado) {
    y += 6;
    y = chk(y, NOTAS_DEL_FORMATO.length * LH + 10);
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9); pdf.text('Notas:', _MG, y); y += LH;
    pdf.setFont('helvetica', 'normal');
    for (const nota of NOTAS_DEL_FORMATO) {
      const ls = pdf.splitTextToSize(`- ${nota}`, _CW);
      y = chk(y, ls.length * 4 + 2); pdf.text(ls, _MG, y); y += ls.length * 4 + 1;
    }
    pintarNotaAlPie(pdf, paginaDeLaLlamada);
  }

  return pdf.output('blob');
};

export const descargarBlob = (blob: Blob, nombre: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `${nombre}.pdf`; a.click();
  URL.revokeObjectURL(url);
};
